/**
 * AskDataPanel — standalone natural-language HCM data chatbot.
 *
 * Each user turn POSTs to /api/copilot/ask-data; results render inline as tables
 * or guidance. Intentionally isolated from SQL Studio and other copilots.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Send,
  Loader2,
  AlertCircle,
  Bot,
  User as UserIcon,
  RotateCcw,
  FileSpreadsheet,
  Database,
  HelpCircle,
  Download,
  Clock,
  Lightbulb,
} from 'lucide-react';
import { getApiBase } from '../apiBase';
import BrainstormIcon from './BrainstormIcon';
import OrcaLogo from '../OrcaLogo';
import ConnectionRequiredNotice from '../ConnectionRequiredNotice';

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 180000,
});

const STARTERS = [
  'Show me a list of all active employees',
  'Find absences in the last 30 days',
  'Which absence types are configured on the tenant?',
  'Headcount by department',
];

function fmtCount(n) {
  return Number.isFinite(n) ? n.toLocaleString() : '0';
}

function downloadCsv(filename, columns, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [columns.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function ToolBadge({ tool, source }) {
  if (tool === 'run_bip_report') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-700/60 bg-emerald-950/50 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
        <FileSpreadsheet className="h-3 w-3" />
        BIP Report
        {source?.name && <span className="ml-1 max-w-[18rem] truncate font-normal text-emerald-400">· {source.name}</span>}
      </span>
    );
  }
  if (tool === 'query_hcm_rest') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-cyan-700/60 bg-cyan-950/50 px-2 py-0.5 text-[11px] font-medium text-cyan-300">
        <Database className="h-3 w-3" />
        HCM REST
        {source?.name && <span className="ml-1 font-normal text-cyan-400">· {source.name}</span>}
      </span>
    );
  }
  if (tool === 'compose_and_run_sql') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-indigo-700/60 bg-indigo-950/50 px-2 py-0.5 text-[11px] font-medium text-indigo-300">
        <Database className="h-3 w-3" />
        OTBI Logical SQL
        {source?.subjectArea && (
          <span className="ml-1 max-w-[18rem] truncate font-normal text-indigo-400">· {source.subjectArea}</span>
        )}
      </span>
    );
  }
  if (tool === 'troubleshooting_guidance') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-violet-700/60 bg-violet-950/50 px-2 py-0.5 text-[11px] font-medium text-violet-300">
        <Lightbulb className="h-3 w-3" />
        Guidance
        {source?.patternIds?.length > 0 && (
          <span className="ml-1 font-normal text-violet-400">· {source.patternIds.join(', ')}</span>
        )}
      </span>
    );
  }
  if (tool === 'sql_library_recipe') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-indigo-700/60 bg-indigo-950/50 px-2 py-0.5 text-[11px] font-medium text-indigo-300">
        <Database className="h-3 w-3" />
        Oracle SQL (recipe)
        {source?.recipeId && (
          <span className="ml-1 max-w-[18rem] truncate font-normal text-indigo-400">· {source.recipeId}</span>
        )}
      </span>
    );
  }
  if (tool === 'ask_clarification') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-700/60 bg-amber-950/50 px-2 py-0.5 text-[11px] font-medium text-amber-300">
        <HelpCircle className="h-3 w-3" />
        Needs more info
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-rose-700/60 bg-rose-950/50 px-2 py-0.5 text-[11px] font-medium text-rose-300">
      <AlertCircle className="h-3 w-3" />
      Error
    </span>
  );
}

function ResultCard({ result, question }) {
  const t = result?.table;
  const hasTable = t && Array.isArray(t.columns) && t.columns.length > 0 && t.rows.length > 0;

  return (
    <div className="rounded-xl border border-orca bg-orca-panel shadow-sm">
      <div className="flex flex-wrap items-center gap-2 border-b border-orca px-4 py-2.5">
        <ToolBadge tool={result.tool} source={result.source} />
        {hasTable && (
          <span className="text-xs text-orca-muted">
            {fmtCount(t.rowCount)} row{t.rowCount === 1 ? '' : 's'}
            {t.truncated && ` (showing first ${t.rows.length})`}
          </span>
        )}
        {result.elapsedMs != null && (
          <span className="flex items-center gap-1 text-xs text-orca-muted">
            <Clock className="h-3 w-3" />
            {(result.elapsedMs / 1000).toFixed(1)}s
          </span>
        )}
        {hasTable && (
          <button
            type="button"
            onClick={() =>
              downloadCsv(
                `askdata-${Date.now()}.csv`,
                t.columns,
                t.rows
              )
            }
            className="ml-auto flex items-center gap-1 rounded-md border border-orca bg-orca-raised px-2 py-1 text-[11px] font-medium text-orca-nav hover:brightness-110"
          >
            <Download className="h-3 w-3" />
            CSV
          </button>
        )}
      </div>

      <div className="px-4 py-2.5 text-sm text-orca-body">
        <div className={result.tool === 'troubleshooting_guidance' ? 'whitespace-pre-wrap leading-relaxed' : ''}>
          {result.message}
        </div>
        {result.reasoning && (
          <div className="mt-1 text-xs italic text-orca-muted">Why this tool: {result.reasoning}</div>
        )}
        {result.source?.queryString && (
          <div className="mt-1 break-all font-mono text-[11px] text-orca-muted">
            q={result.source.queryString}
          </div>
        )}
        {result.source?.path && (
          <div className="mt-1 break-all font-mono text-[11px] text-orca-muted">{result.source.path}</div>
        )}
        {result.source?.sql && (
          <details className="mt-1.5">
            <summary className="cursor-pointer text-[11px] font-medium text-indigo-400 hover:underline">
              Show generated Logical SQL
            </summary>
            <pre className="mt-1 max-h-48 overflow-auto rounded-md bg-orca-base p-2 font-mono text-[11px] text-orca-muted">
              {result.source.sql}
            </pre>
          </details>
        )}
      </div>

      {hasTable ? (
        <div className="max-h-[26rem] overflow-auto border-t border-orca">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-orca-raised text-orca-nav">
              <tr>
                {t.columns.map((c, i) => (
                  <th key={i} className="border-b border-orca px-2.5 py-1.5 text-left font-semibold">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {t.rows.map((row, r) => (
                <tr key={r} className="odd:bg-orca-panel even:bg-orca-base">
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      className="border-b border-orca px-2.5 py-1 align-top text-orca-nav"
                      title={String(cell || '')}
                    >
                      <div className="max-w-[18rem] truncate">{cell}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : result.tool === 'run_bip_report' ||
        result.tool === 'query_hcm_rest' ||
        result.tool === 'compose_and_run_sql' ||
        result.tool === 'sql_library_recipe' ? (
        <div className="border-t border-orca px-4 py-3 text-xs text-orca-muted">
          Tool ran but returned no rows for: <span className="italic">"{question}"</span>
        </div>
      ) : null}
    </div>
  );
}

export default function AskDataPanel({
  title,
  subtitle,
  standalone,
  askDataProvider,
  hcmConnection = null,
  onOpenConnections,
}) {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [turns, setTurns] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, sending]);

  const providerName = askDataProvider?.selected || 'mock';
  const providerModel = askDataProvider?.model || '';
  const isMock = providerName === 'mock' || askDataProvider?.configured === false;
  const hcmReady = hcmConnection?.isConfigured === true;

  async function send(rawText) {
    const text = String(rawText ?? input).trim();
    if (!text || sending) return;
    if (!hcmReady) {
      setError('Connect a Fusion pod under Connections before asking data questions.');
      return;
    }
    setError('');
    setInput('');

    const turnId = `t-${Date.now()}`;
    setTurns((prev) => [...prev, { id: turnId, question: text, result: null }]);
    setSending(true);

    try {
      const res = await API.post('/api/copilot/ask-data', { question: text }, { validateStatus: () => true });
      if (res.status === 200) {
        setTurns((prev) => prev.map((t) => (t.id === turnId ? { ...t, result: res.data } : t)));
      } else {
        const errMsg = res.data?.error || `HTTP ${res.status}`;
        setTurns((prev) =>
          prev.map((t) =>
            t.id === turnId
              ? { ...t, result: { tool: 'error', message: errMsg, table: null, source: null } }
              : t
          )
        );
      }
    } catch (err) {
      setError(err.message || 'Ask Data failed');
      setTurns((prev) =>
        prev.map((t) =>
          t.id === turnId
            ? { ...t, result: { tool: 'error', message: err.message, table: null, source: null } }
              : t
        )
      );
    } finally {
      setSending(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    send();
  }

  function reset() {
    setTurns([]);
    setError('');
  }

  const empty = turns.length === 0;

  return (
    <div className="orca-ask-data-panel flex h-full min-h-0 flex-1 flex-col rounded-xl border border-orca bg-orca-panel shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-orca px-5 py-3 rounded-t-xl">
        <div className="flex items-center gap-3 min-w-0">
          <OrcaLogo className="h-9 w-9" />
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-orca-heading truncate">
              {title || 'Ask Data'}
            </h2>
            {subtitle ? (
              <p className="text-xs text-orca-muted truncate">{subtitle}</p>
            ) : isMock ? (
              <p className="text-xs text-amber-400 truncate">
                Mock provider — set ASK_DATA_LLM_PROVIDER=deepseek and DEEPSEEK_API_KEY.
              </p>
            ) : (
              <p className="text-xs text-orca-muted truncate">
                {providerName}{providerModel ? ` · ${providerModel}` : ''}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={reset}
          className="flex items-center gap-1.5 rounded-lg border border-orca bg-orca-raised px-3 py-1.5 text-xs font-medium text-orca-nav hover:brightness-110"
          title="Clear and start over"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          New session
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
        {!hcmReady && (
          <ConnectionRequiredNotice onOpenConnections={onOpenConnections} />
        )}
        {empty && (
          <div className="mx-auto max-w-md text-center text-orca-muted">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-orca-raised text-cyan-400">
              <BrainstormIcon className="h-7 w-7" />
            </div>
            <p className="text-sm">
              {standalone
                ? 'Ask a question about your HCM data — this chatbot runs independently of other copilots.'
                : 'Ask a question or pick a starter below.'}
            </p>
            <div className="mt-5 grid gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  className="orca-ask-data-starter rounded-lg border border-orca bg-orca-raised px-3 py-2 text-left text-sm hover:border-cyan-600 hover:brightness-110"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {turns.map((t) => (
          <div key={t.id} className="space-y-3">
            <div className="flex justify-end gap-3">
              <div className="max-w-[78%] rounded-2xl bg-cyan-700 px-4 py-2.5 text-sm text-white">
                {t.question}
              </div>
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orca-raised text-orca-nav">
                <UserIcon className="h-4 w-4" />
              </div>
            </div>

            <div className="flex justify-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orca-raised text-cyan-400">
                <Bot className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                {t.result ? (
                  <ResultCard result={t.result} question={t.question} />
                ) : (
                  <div className="flex items-center gap-2 rounded-xl bg-orca-raised px-4 py-2.5 text-sm text-orca-muted shadow-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Thinking…
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {error && (
          <div className="flex items-start gap-2 rounded-lg bg-rose-950/50 border border-rose-800/60 p-3 text-sm text-rose-300">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="break-words">{error}</div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="border-t border-orca bg-orca-panel p-3 rounded-b-xl">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder='Try: "Show employees in Marketing" or "Absences last 30 days"'
            disabled={sending}
            className="flex-1 resize-none rounded-lg border border-orca bg-orca-raised px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-600 text-white shadow-sm hover:bg-cyan-500 disabled:bg-orca-border"
            title="Send (Enter)"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}
